import { useEffect, useState } from "react";

export type TocItem = {
  id: string;
  label: string;
};

type TableOfContentsProps = {
  items: TocItem[];
  /** Heading shown above the list. Defaults to "On this page". */
  title?: string;
};

/**
 * Sticky "On this page" navigation with scroll-spy.
 * - lg+: renders as a sticky sidebar aside (caller places it in a grid column).
 * - below lg: renders inline as a bordered card at the top of the content.
 * Anchor clicks use native #id scrolling.
 */
export default function TableOfContents({ items, title = "On this page" }: TableOfContentsProps) {
  const [activeId, setActiveId] = useState<string>(items[0]?.id ?? "");

  useEffect(() => {
    if (items.length === 0) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible[0]) setActiveId(visible[0].target.id);
      },
      // Bias the active zone toward the top of the viewport.
      { rootMargin: "-96px 0px -60% 0px", threshold: 0 }
    );

    for (const item of items) {
      const el = document.getElementById(item.id);
      if (el) observer.observe(el);
    }

    return () => observer.disconnect();
  }, [items]);

  return (
    <nav aria-label={title} className="text-sm">
      <p className="mb-3 text-xs font-semibold uppercase tracking-[0.14em] text-text-faint">
        {title}
      </p>
      <ul className="space-y-1 border-l border-border">
        {items.map((item) => {
          const active = item.id === activeId;
          return (
            <li key={item.id}>
              <a
                href={`#${item.id}`}
                aria-current={active ? "location" : undefined}
                className={`-ml-px block border-l-2 py-1 pl-3 leading-snug transition ${
                  active
                    ? "border-primary font-medium text-primary-text"
                    : "border-transparent text-text-muted hover:border-border hover:text-text"
                }`}
              >
                {item.label}
              </a>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}
